/* eslint-disable @typescript-eslint/no-shadow  */
import qs from 'qs';
import Vue from 'vue';

export const state = () => ({
  companyInScope: '',
  companies: {},
  companiesBasicInfo: {}, // TODO: merge with companies with /customer drop
  companiesStats: {},
  usersForCompany: {},
});

export const getters = {
  // Generic getters
  byId: (state) => (companyId) => state.companies[companyId],
  basicInfoById: (state) => (companyId) => state.companiesBasicInfo[companyId],
  statsById: (state) => (companyId) => state.companiesStats[companyId],
  getUsersForCompany: (state) => (companyId) =>
    state.usersForCompany[companyId] || [],

  // Getters for company in scope
  inScope: (state, getters) => getters.byId(state.companyInScope) || {},
  basicInfoInScope: (state, getters) =>
    getters.basicInfoById(state.companyInScope) || {},
  statsInScope: (state, getters) =>
    getters.statsById(state.companyInScope) || {},
};

export const actions = {
  /**
   * Company
   */
  fetchCompanyBasicInfo({ dispatch, state }, { companyId }) {
    if (companyId in state.companiesBasicInfo) {
      return Promise.resolve();
    }

    return dispatch('getCompanyBasicInfo', companyId);
  },
  getCompanyBasicInfo({ commit }, companyId) {
    return this.$axios
      .get(`/companies/v1/company/${companyId}/basic-info`, {
        baseURL: `${process.env.API_URL}`,
      })
      .then((res) => {
        commit('setCompanyBasicInfo', {
          companyId,
          info: res.data,
        });

        return res.data;
      });
  },
  getStats({ commit, state }, companyId) {
    const realCompanyId = companyId || state.companyInScope;

    return this.$axios
      .get(`/companies/v1/company/${realCompanyId}/stats`, {
        baseURL: `${process.env.API_URL}`,
      })
      .then((res) => {
        commit('setCompanyStats', {
          companyId: realCompanyId,
          stats: res.data,
        });

        return res.data;
      });
  },
  getCompanyUsers({ commit }, { companyId }) {
    return this.$axios
      .get(`/user/getUsersByCompanyId/${companyId}`)
      .then((res) => {
        commit('setCompanyUsers', {
          companyId,
          users: res.data,
        });
      });
  },
  async createSecondaryCompany(_, { companyId, newCompanyData }) {
    const { data } = await this.$axios.post(
      `/companies/v1/company/${companyId}/secondary`,
      {
        ...newCompanyData,
      },
      {
        baseURL: `${process.env.API_URL}`,
      }
    );

    return data;
  },
  toggleSetting({ commit }, { companyId, key, value }) {
    return this.$axios
      .patch(
        `/companies/v1/company/${companyId}/settings`,
        {
          key,
          value,
        },
        {
          baseURL: `${process.env.API_URL}`,
        }
      )
      .then(() => {
        commit('updateGenericSetting', {
          companyId,
          key,
          value,
        });
      });
  },

  /**
   * Companies, Bulk operations
   */
  async getCompanies(_, { params }) {
    const {
      text,
      status,
      byAdvisor = null,
      isPrimary = null,
      role = null,
      showTest = false,
    } = params;
    const query = qs.stringify({
      text,
      status,
      showTest,
      role,
      byAdvisor,
      isPrimary,
    });

    const { data: companies } = await this.$axios.get(
      `/companies/v1/list?${query}`,
      {
        baseURL: process.env.API_URL,
      }
    );

    return companies;
  },

  reassignCorePlan(_, { companyId, coupon, planValue, prorate, resetCycle }) {
    return this.$axios.post(
      `/billing/billing/v2/core-plan-reassignment`,
      {
        companyId,
        coupon,
        planValue,
        prorate,
        resetCycle,
      },
      {
        baseURL: `${process.env.PUBLICAPI_URL}`,
      }
    );
  },

  bulkCorePlanReassignment(_, { url }) {
    return this.$axios.post(
      `/billing/billing/v2/bulk-core-plan-reassignment`,
      {
        url,
      },
      {
        baseURL: `${process.env.PUBLICAPI_URL}`,
      }
    );
  },

  bulkUpdateCancellations(_, { url }) {
    return this.$axios.post(
      `/billing/billing/v2/bulk-update-cancellations`,
      {
        url,
      },
      {
        baseURL: `${process.env.PUBLICAPI_URL}`,
      }
    );
  },

  bulkTrackStaffSeatsUsage() {
    return this.$axios.post(
      `/billing/billing/v2/internal/staff-seats/sync-usage`,
      {},
      {
        baseURL: `${process.env.PUBLICAPI_URL}`,
      }
    );
  },

  bulkRepairAddonsSeats() {
    return this.$axios.post(
      `/billing/billing/v2/internal/staff-seats/repair-pairing`,
      {},
      {
        baseURL: `${process.env.PUBLICAPI_URL}`,
      }
    );
  },
};

export const mutations = {
  setCompanyInScope(state, companyId) {
    state.companyInScope = companyId;
  },
  // TODO: request through /companies, not /customer or other places
  setCompany(state, { company }) {
    if (company && company._id) {
      Vue.set(state.companies, company._id, company);
    }
  },
  setCompanyPartial(state, { companyId, partial }) {
    if (!(companyId in state.companies)) {
      state.companies[companyId] = {};
    }

    Object.entries(partial).forEach(([key, value]) => {
      Vue.set(state.companies[companyId], key, value);
    });
  },
  setCompanyBasicInfo(state, { companyId, info }) {
    Vue.set(state.companiesBasicInfo, companyId, info);
  },

  setCompanyStats(state, { companyId, stats }) {
    Vue.set(state.companiesStats, companyId, stats);
  },
  setCompanyUsers(state, { companyId, users }) {
    Vue.set(state.usersForCompany, companyId, users);
  },
  updateDeliquentStatus(state, { companyId, status }) {
    const company = state.companies[companyId];
    Vue.set(company, 'delinquent', status.delinquent);
    Vue.set(company.stripe.customer, 'delinquent', status.delinquent);
    Vue.set(
      company.stripe,
      'original_payment_date',
      status.original_payment_date
    );
    Vue.set(
      company.stripe,
      'expected_payment_date',
      status.expected_payment_date
    );
    Vue.set(company.stripe, 'attempt_count', status.attempt_count);
  },
  updateGenericSetting(state, { companyId, key, value }) {
    const company = state.companies[companyId];
    Vue.set(company.settings, key, value);
  },
};
